import {
  RenderFormat,
  ViewerDocumentKind,
  ViewerDownloadTargetKind,
  ViewerOpenContextKind,
  type ContentViewerSession,
  type FileContentRendererConfig,
} from '@/content-viewer/store/schemas'
import type { PaxtonHighlightArea } from '@/components/react-pdf-viewer/schema/paxton-highlight-area'
import { buildLegacyFileViewerSession } from '@/routes/dashboard/files/utils/build-legacy-file-viewer-session'
import { buildUserFileRendererConfig } from '@/user_files/files/utils/build-user-file-renderer-config'
import { buildUserFileViewerSession } from '@/user_files/files/utils/build-user-file-viewer-session'
import { createViewerSession } from '@/content-viewer/utils/create-viewer-session'
import type { UserFileRead } from '@/uppy-file-upload/schemas'
import { type MedSummaryReference } from '@/medical-billing-summaries/references/store/schemas'
import { MedSummarySourceProvider, type MedSummarySourceRef } from './schemas'

type BuildMedSummaryViewerSessionArgs = {
  source: MedSummarySourceRef
  reference?: MedSummaryReference
  userFile?: UserFileRead | null
  highlights?: PaxtonHighlightArea[]
  startingPage?: number
  // Asset surface, non-PDF source: open the stored conversion the file was extracted
  // against (AI-94) so highlight geometry lines up. Download still serves the original.
  convertedPdf?: { assetId: string; userFileId: string }
}

function buildLegacyRenderer(
  source: Extract<MedSummarySourceRef, { provider: MedSummarySourceProvider.LEGACY }>,
  highlights?: PaxtonHighlightArea[],
  startingPage?: number
): FileContentRendererConfig {
  return {
    format: RenderFormat.PDF,
    filePath: source.file_path,
    ...(highlights ? { highlights } : {}),
    ...(startingPage !== undefined ? { startingPage } : {}),
  }
}

export function buildMedSummaryViewerSession(args: BuildMedSummaryViewerSessionArgs): ContentViewerSession | null {
  const { source, reference, userFile, highlights, startingPage, convertedPdf } = args

  if (convertedPdf && source.provider === MedSummarySourceProvider.USER_FILES) {
    return createViewerSession({
      document: {
        kind: ViewerDocumentKind.MED_SUMMARY_CONVERTED_PDF,
        assetId: convertedPdf.assetId,
        userFileId: convertedPdf.userFileId,
        title: source.display_name,
        downloadTarget: {
          kind: ViewerDownloadTargetKind.USER_FILE_ORIGINAL,
          userFileId: convertedPdf.userFileId,
        },
      },
      context: reference
        ? {
            kind: ViewerOpenContextKind.MED_SUMMARY_REFERENCE,
            referenceId: reference.id,
            sourceId: source.source_id,
          }
        : {
            kind: ViewerOpenContextKind.MED_SUMMARY_SOURCE,
            sourceId: source.source_id,
          },
      presentation: {
        ...(highlights ? { pdfHighlights: highlights } : {}),
        ...(startingPage !== undefined ? { startingPage } : {}),
      },
    })
  }

  if (source.provider === MedSummarySourceProvider.LEGACY) {
    const isReferenceBacked = Boolean(reference)
    return buildLegacyFileViewerSession({
      id: source.source_id,
      title: source.display_name,
      renderer: buildLegacyRenderer(source, highlights, startingPage),
      context: reference
        ? {
            kind: ViewerOpenContextKind.MED_SUMMARY_REFERENCE,
            referenceId: reference.id,
            sourceId: source.source_id,
          }
        : {
            kind: ViewerOpenContextKind.MED_SUMMARY_SOURCE,
            sourceId: source.source_id,
          },
      downloadable: !isReferenceBacked,
    })
  }

  if (!userFile) return null

  const baseRenderer = buildUserFileRendererConfig(userFile)
  if (!baseRenderer) return null

  // Med source highlighting is PDF-only today. Non-PDF sources need a separate
  // presentation primitive before highlights or startingPage can apply.
  const renderer =
    baseRenderer.format === RenderFormat.PDF
      ? {
          ...baseRenderer,
          ...(highlights ? { highlights } : {}),
          ...(startingPage !== undefined ? { startingPage } : {}),
        }
      : baseRenderer

  return buildUserFileViewerSession({
    userFile,
    renderer,
    context: reference
      ? {
          kind: ViewerOpenContextKind.MED_SUMMARY_REFERENCE,
          referenceId: reference.id,
          sourceId: source.source_id,
        }
      : {
          kind: ViewerOpenContextKind.MED_SUMMARY_SOURCE,
          sourceId: source.source_id,
        },
  })
}
